import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import {
  type UpdateMeetingParams,
  useScheduledMeetingsMutations,
  useUser,
} from '../../../contexts';
import { useCalendarTypes } from '../../../hooks/useCalendarTypes';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { calendarService } from '../../../services/calendarService';
import { createMeetingAccessPayload } from '../../../utils/meetingAccessPayload';
import { redirectToCalendarEdit } from '../../CalendarRecentPanel/helpers/redirectToCalendarEdit';
import {
  CALENDAR_TYPE_GOOGLE,
  CALENDAR_TYPE_OUTLOOK,
  SCHEDULE_MEETING_DRAFT_QUERY_KEY,
} from '../helpers/constants';
import { type MeetingFormErrors, validateMeetingForm } from '../helpers/validators';
import type { CreatedCalendarEvent, UseScheduleMeetingSubmitProps } from '../types';
import useCalendarLinkGenerator from './useCalendarLinkGenerator';

const useScheduleMeetingSubmit = ({
  formData,
  onSuccess,
  onClose,
  phoneNumber,
  pin,
  hasLicenseError,
}: UseScheduleMeetingSubmitProps) => {
  const queryClient = useQueryClient();
  const { update, isUpdating } = useScheduledMeetingsMutations();
  const { handleApiError } = useErrorHandler('ScheduleMeetingModal');
  const [validationErrors, setValidationErrors] = useState<MeetingFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openGoogleEventPopup, openOutlookEventPopup } = useCalendarLinkGenerator();
  const { userEmail } = useUser();
  const { data: accountConnections } = useCalendarTypes();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    const validationErrs = validateMeetingForm(formData);
    setValidationErrors(validationErrs);

    if (Object.keys(validationErrs).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const userDomain = userEmail?.split('@')[1] || '';
      const typePayload = createMeetingAccessPayload(formData.meetingType, userDomain);

      // A meeting's `description` is its personal-meeting name, owned by the
      // Personal Meetings modal, and PATCH treats null as "clear it" — sending it
      // here erased the name of whichever room was being scheduled. Note the
      // unrelated `formData.description`, which is the calendar event subject and
      // reaches the event payload rather than the room.
      const meetingRequest: UpdateMeetingParams = {
        room: formData.room,
        password: formData?.password || '',
        ...typePayload,
      };
      const updatedMeeting = await update(meetingRequest);
      let createdCalendarEvent: CreatedCalendarEvent | null = null;

      const dialIn = hasLicenseError
        ? { hasLicenseError: true }
        : phoneNumber != null || pin != null
          ? { phoneNumber: phoneNumber ?? null, pin: pin ?? null }
          : undefined;
      if (
        formData.calendarOption === CALENDAR_TYPE_OUTLOOK ||
        formData.calendarOption === CALENDAR_TYPE_GOOGLE
      ) {
        try {
          createdCalendarEvent = await calendarService.createCalendarEventInBackground({
            calendarOption: formData.calendarOption,
            formData,
            dialIn,
            accountConnections,
          });
        } catch (calendarError) {
          console.error(
            '[ScheduleMeetingModal] Failed to create background calendar event:',
            calendarError,
          );
        }

        if (createdCalendarEvent?.id) {
          const redirectEvent = createdCalendarEvent;
          redirectToCalendarEdit(
            redirectEvent,
            accountConnections?.microsoft?.login_link,
            accountConnections?.microsoft?.email,
          );
        } else if (formData.calendarOption === CALENDAR_TYPE_OUTLOOK) {
          openOutlookEventPopup(formData, dialIn, accountConnections?.microsoft?.email);
        } else if (formData.calendarOption === CALENDAR_TYPE_GOOGLE) {
          openGoogleEventPopup(formData, dialIn);
        }
      }

      const dialInForCallback = hasLicenseError
        ? { hasLicenseError: true }
        : phoneNumber != null || pin != null
          ? { phoneNumber: phoneNumber ?? null, pin: pin ?? null, hasLicenseError: false }
          : undefined;

      await Promise.resolve(
        onSuccess?.({
          meeting: {
            ...updatedMeeting,
            description: updatedMeeting.description ?? null,
            password: updatedMeeting.password ?? '',
            restricted: updatedMeeting.restricted ?? false,
          },
          dialIn: dialInForCallback,
          createdCalendarEvent: createdCalendarEvent ?? undefined,
          calendarOption: formData.calendarOption,
        }),
      );
      onClose();

      queryClient.removeQueries({ queryKey: SCHEDULE_MEETING_DRAFT_QUERY_KEY });
    } catch (error) {
      const errorInstance = error instanceof Error ? error : new Error(String(error));
      setSubmitError(errorInstance.message);
      handleApiError(errorInstance, 'scheduleMeeting', 'POST');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    validationErrors,
    submitError,
    isScheduling: isUpdating || isSubmitting,
  };
};

export default useScheduleMeetingSubmit;
